/**
 * useSocketIO Hook
 *
 * React hook for Socket.IO connection management.
 * Provides easy access to connection state and event subscription.
 *
 * @example
 * ```tsx
 * function NotificationListener() {
 *   const { subscribe, isConnected, connectionState } = useSocketIO();
 *
 *   useEffect(() => {
 *     const unsubscribe = subscribe(
 *       SocketIOEvent.NotificationReceived,
 *       (notification) => {
 *         toast.info(notification.message);
 *       }
 *     );
 *
 *     return unsubscribe;
 *   }, [subscribe]);
 *
 *   return <div>Status: {connectionState}</div>;
 * }
 * ```
 */

import { useEffect, useState, useCallback } from "react";
import socketIOManager, { ConnectionState, EventHandler } from "@/lib/socketio";
import { useAuth } from "./useAuth";

export interface UseSocketIOReturn {
  // State
  connectionState: ConnectionState;
  isConnected: boolean;
  isConnecting: boolean;
  isDisconnected: boolean;
  isReconnecting: boolean;

  // Actions
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  subscribe: <T = any>(event: string, handler: EventHandler<T>) => () => void;

  send: (event: string, ...args: any[]) => Promise<void>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  invoke: <T = any>(event: string, ...args: any[]) => Promise<T>;
}

/**
 * Hook to manage Socket.IO connection
 */
export const useSocketIO = (): UseSocketIOReturn => {
  const { isAuthenticated, user } = useAuth();
  const [connectionState, setConnectionState] = useState<ConnectionState>(
    socketIOManager.getState(),
  );

  // Initialize connection when user is authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      let cancelled = false;

      // Defer to avoid state updates during mount — same pattern as authStore.initializeAuth
      queueMicrotask(() => {
        if (cancelled) return;

        // Get access token from localStorage.
        // Zustand persist wraps state as { state: {...}, version: 0 },
        // so the token is at parsed.state.accessToken, not parsed.accessToken.
        const authData = localStorage.getItem("wicloud_auth");
        let accessToken: string | undefined;

        if (authData) {
          try {
            const parsed = JSON.parse(authData);
            accessToken = parsed?.state?.accessToken ?? parsed?.accessToken;
          } catch (error) {
            console.error("Failed to parse auth data:", error);
          }
        }

        // Initialize connection
        socketIOManager.initialize(accessToken).catch((error) => {
          console.error("Failed to initialize Socket.IO:", error);
        });
      });

      // Subscribe to state changes
      const unsubscribe = socketIOManager.onStateChange((state) => {
        setConnectionState(state);
      });

      return () => {
        cancelled = true;
        unsubscribe();
      };
    } else {
      // Disconnect when user logs out
      socketIOManager.disconnect().catch((error) => {
        console.error("Failed to disconnect Socket.IO:", error);
      });
    }
  }, [isAuthenticated, user]);

  // Subscribe to events
  const subscribe = useCallback(
    <T = unknown>(event: string, handler: EventHandler<T>) => {
      return socketIOManager.subscribe(event, handler);
    },
    [],
  );

  // Send message
  const send = useCallback(async (event: string, ...args: unknown[]) => {
    return socketIOManager.send(event, ...args);
  }, []);

  // Invoke method
  const invoke = useCallback(
    async <T = unknown>(event: string, ...args: unknown[]) => {
      return socketIOManager.invoke<T>(event, ...args);
    },
    [],
  );

  return {
    connectionState,
    isConnected: connectionState === ConnectionState.Connected,
    isConnecting: connectionState === ConnectionState.Connecting,
    isDisconnected: connectionState === ConnectionState.Disconnected,
    isReconnecting: connectionState === ConnectionState.Reconnecting,
    subscribe,
    send,
    invoke,
  };
};
